import React, { useEffect, useState } from 'react';
import { Divider } from '@mui/material';
import StaffBasicForm from './components/StaffBasicForm';
import { ClassInfo, TeacherProfile, UpdateStaffDetailProps } from './types';
import users from '../../apis/users';
import { SnackbarSeverityType } from '../../enums';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import { AllowedRoleToAccessAY } from './config/constant';
import {
  RoundedButton,
  SaveActionButtonWrapper,
  SectionTitleText
} from './styled';
import AddClassForm from './components/AddClassForm';
import AcademicYearClassTable from './components/AcademicYearClassTable';
import EditClassForm from './components/EditClassForm';
import StaffBasicFormSkeleton from './components/StaffBasicFormSkeleton';
import { transformStaffDetailData } from './utils/transformApiResponse';
import stringUtils from '../../utils/stringUtils';
import { PageWrapper } from '../common/styled';

const StaffDetailPage = ({
  selectedTeacherId,
  showToast,
  handleRowEdit
}: {
  selectedTeacherId: string;
  showToast: (message: string, severity: SnackbarSeverityType) => void;
  handleRowEdit: (id?: string) => void;
}) => {
  const [staffDetailData, setStaffDetailData] = useState<TeacherProfile>();
  const [isLoading, setIsLoading] = useState(false);

  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [isAddClassModalOpen, setIsAddClassModalOpen] = useState(false);
  const [isEditClassModalOpen, setIsEditClassModalOpen] = useState(false);

  const [selectedClassData, setSelectedClassData] = useState<ClassInfo>();
  const [formData, setFormData] = useState<UpdateStaffDetailProps>();

  const fetchStaffDetail = async () => {
    setIsLoading(true);
    try {
      const response = await users.fetchUserById(selectedTeacherId);

      const transformedUserDetail = transformStaffDetailData(response.data);

      setStaffDetailData(transformedUserDetail);
      if (transformedUserDetail) {
        setFormData({
          name: transformedUserDetail.name || '',
          email: transformedUserDetail.email || '',
          currentActivityClubs:
            transformedUserDetail?.currentCourseInfo?.activityClubs || [],
          memberCode: transformedUserDetail.memberCode || ''
        });
      }
    } catch (error: any) {
      showToast(
        error.error?.[0].message || 'Error getting staff detail',
        SnackbarSeverityType.error
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStaffDetail();
    // eslint-disable-next-line
  }, [selectedTeacherId]);

  const handleOpenAddClassModal = () => {
    setIsAddClassModalOpen(true);
  };

  const handleCloseAddClassModal = () => {
    setIsAddClassModalOpen(false);
  };

  const handleOpenEditClassModal = (Id: string) => {
    const [grade, division] = decodeURIComponent(Id).split('|');
    const filteredClass = staffDetailData?.currentCourseInfo.classes.find(
      (cls: any) => cls.grade === grade && cls.division === division
    );

    if (filteredClass) {
      setSelectedClassData(filteredClass);
      setIsEditClassModalOpen(true);
    }
  };

  const handleCloseEditClassModal = () => {
    setIsEditClassModalOpen(false);
  };

  const handleStaffDetailUpdate = async () => {
    setIsLoading(true);
    try {
      if (selectedTeacherId && formData) {
        await users.updateStaffDetail(selectedTeacherId, formData);
        showToast(
          'Staff details updated successfully',
          SnackbarSeverityType.success
        );
        fetchStaffDetail();
        setConfirmationDialogOpen(false);
      }
    } catch (error: any) {
      showToast(
        error.errors?.[0]?.message || 'Error updating staff details',
        SnackbarSeverityType.error
      );
    } finally {
      setIsLoading(false);
    }
  };

  const isUserATeachingStaff = AllowedRoleToAccessAY.includes(
    staffDetailData?.role || ''
  );

  return (
    <>
      <PageWrapper>
        <SectionTitleText>
          {stringUtils.formatStringToReadAbleText(staffDetailData?.role || '')}{' '}
          Profile Details
        </SectionTitleText>
        {isLoading ? (
          <StaffBasicFormSkeleton />
        ) : (
          <StaffBasicForm
            isUserTeacherStaff={isUserATeachingStaff}
            staffDetailData={staffDetailData}
            setFormData={setFormData}
            formData={formData}
            showToast={showToast}
            onStatusUpdate={fetchStaffDetail}
          />
        )}

        <Divider />
        {isUserATeachingStaff && (
          <AcademicYearClassTable
            staffDetailData={staffDetailData}
            handleOpenAddClassModal={handleOpenAddClassModal}
            handleOpenEditClassModal={handleOpenEditClassModal}
            onDeleteClassSuccess={fetchStaffDetail}
            showToast={showToast}
            isLoading={isLoading}
          />
        )}
      </PageWrapper>
      <PageWrapper style={{ minHeight: 'auto', padding: 16 }}>
        <SaveActionButtonWrapper>
          <RoundedButton onClick={() => handleRowEdit()}>Back</RoundedButton>
          <RoundedButton onClick={() => setConfirmationDialogOpen(true)}>
            Save
          </RoundedButton>
        </SaveActionButtonWrapper>
      </PageWrapper>

      <AddClassForm
        onAddNewClassSuccess={fetchStaffDetail}
        staffDetailData={staffDetailData}
        showToast={showToast}
        onClose={handleCloseAddClassModal}
        open={isAddClassModalOpen}
      />
      <EditClassForm
        selectedClassData={selectedClassData}
        onEditClassSuccess={fetchStaffDetail}
        staffDetailData={staffDetailData}
        showToast={showToast}
        onClose={handleCloseEditClassModal}
        open={isEditClassModalOpen}
      />
      <ConfirmationDialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        onConfirm={handleStaffDetailUpdate}
        message={'Are you sure you want save?'}
      />
    </>
  );
};

export default StaffDetailPage;
